import { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { buildItems, itemStats, layout, maxLanesFor, minToTime, type PlacedItem } from './calendarLayout';
import { CAL_TYPE_ORDER, calType, type BookableType, type CalItem, type CalSession } from './calendarTypes';
import { EventCard, MoreButton, MorePopover, RailStrip, RunCard, heightFor, laneStyle, topFor, type Geometry } from './TimelineParts';
import { BusyPill, Segmented, WeekNav } from './CalendarControls';
import { SessionDrawer } from './SessionDrawer';
import type { PulseDay } from './WeekPulse';

/* The full timeline: a near-fullscreen modal over the Calendar tab.
 *
 * Week mode shows seven day columns with lanes and "+N" overflow. Day mode
 * shows one column per resource, which removes most overlaps outright — the
 * same view the Today tab uses. */

const DENSITY = { Compact: 48, Default: 72, Roomy: 104 } as const;
export type Density = keyof typeof DENSITY;

const GUTTER = 56;
/** Width of one rail sub-lane plus its gap. */
const RAIL_STEP = 7;

export interface TimelineInit {
  mode: 'week' | 'day';
  day?: number;
  hour?: number;
}

interface ResourceSpec {
  name: string;
  type: BookableType;
}

interface ColumnSpec {
  key: string;
  items: CalItem[];
  /** Week mode: the day this column stands for. */
  day?: PulseDay;
  /** Day mode: the studio / court / staff member this column stands for. */
  resource?: ResourceSpec;
}

// ─── Column ──────────────────────────────────────────────────────────────────

function TimelineColumn({ items, geo, maxLanes, width, height, selected, today, flash, rail, now, onPick, onMore }: {
  items: CalItem[];
  geo: Geometry;
  maxLanes: number;
  width: number;
  height: number;
  selected: CalItem | null;
  today: boolean;
  flash: boolean;
  /** Week mode parks slot runs on a rail instead of giving them a lane. */
  rail: boolean;
  now: { min: number } | null;
  onPick: (it: CalItem) => void;
  onMore: (m: Extract<CalItem, { kind: 'more' }>, rect: DOMRect) => void;
}) {
  const { cards, runs } = useMemo(() => {
    if (!rail) return { cards: items, runs: [] as PlacedItem[] };
    return {
      cards: items.filter(i => i.kind !== 'run'),
      // Runs get their own lane assignment, up to 6 sub-lanes of rail.
      runs: layout(items.filter(i => i.kind === 'run'), 6),
    };
  }, [items, rail]);

  const railLanes = runs.reduce((a, o) => Math.max(a, o.lanes), 0);
  const railWidth = railLanes ? railLanes * RAIL_STEP + 4 : 0;

  const placed = useMemo(() => layout(cards, maxLanes), [cards, maxLanes]);
  const cardArea = Math.max(40, width - railWidth);

  return (
    <div
      className={`a-tl-col${today ? ' today' : ''}${flash ? ' flash' : ''}`}
      style={{ height, backgroundSize: `100% ${geo.hourPx}px` }}
    >
      <div className="a-tl-main" style={{ right: railWidth }}>
        {placed.map(p => {
          const style = laneStyle(p, geo);
          const w = cardArea / p.lanes;
          if (p.it.kind === 'more') return <MoreButton key={p.it.id} more={p.it} style={style} onOpen={onMore} />;
          if (p.it.kind === 'run') {
            return (
              <RunCard
                key={p.it.id}
                run={p.it}
                style={style}
                width={w}
                selected={selected?.id === p.it.id}
                onPick={() => onPick(p.it)}
              />
            );
          }
          return (
            <EventCard
              key={p.it.id}
              s={p.it.s}
              style={style}
              width={w}
              selected={selected?.id === p.it.id}
              onPick={() => onPick(p.it)}
            />
          );
        })}
      </div>

      {railWidth > 0 && (
        <div className="a-tl-rail" style={{ width: railWidth }}>
          {runs.map(p => {
            if (p.it.kind !== 'run') return null;
            return (
              <RailStrip
                key={p.it.id}
                run={p.it}
                top={topFor(p.it.start, geo)}
                height={heightFor(p.it.start, p.it.end, geo)}
                left={p.lane * RAIL_STEP + 2}
                selected={selected?.id === p.it.id}
                onPick={() => onPick(p.it)}
              />
            );
          })}
        </div>
      )}

      {today && now && <div className="a-tl-now" style={{ top: topFor(now.min, geo) }} />}
    </div>
  );
}

// ─── Modal ───────────────────────────────────────────────────────────────────

export function FullTimelineModal({
  sessions, days, init, weekLabel, hours, now, filters, loading, density, onDensity,
  onClose, onPrevWeek, onNextWeek, onOpenSession, onEditSession,
}: {
  sessions: CalSession[];
  days: PulseDay[];
  init: TimelineInit;
  weekLabel: string;
  hours: [number, number];
  now: { dateStr: string; min: number };
  /** Branch + type filters, rendered from the page's state. */
  filters: React.ReactNode;
  loading: boolean;
  density: Density;
  onDensity: (d: Density) => void;
  onClose: () => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onOpenSession: (s: CalSession) => void;
  onEditSession: (s: CalSession) => void;
}) {
  const [mode, setMode] = useState<'week' | 'day'>(init.mode);
  const [day, setDay] = useState(() => (init.day != null ? init.day : (days.find(d => d.today) || days[0]).i));
  const [selected, setSelected] = useState<CalItem | null>(null);
  const [more, setMore] = useState<{ m: Extract<CalItem, { kind: 'more' }>; rect: DOMRect } | null>(null);
  const [flash, setFlash] = useState<number | null>(init.mode === 'week' && init.day != null ? init.day : null);
  const [containerWidth, setContainerWidth] = useState(1200);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const hourPx = DENSITY[density];
  const geo: Geometry = { startHour: hours[0], hourPx };
  const gridHeight = (hours[1] - hours[0]) * hourPx;

  // Column width drives the lane count, so it has to be measured, not guessed.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setContainerWidth(el.clientWidth));
    ro.observe(el);
    setContainerWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // Open where the click pointed: the clicked hour, or the working day's start.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const h = init.hour != null ? init.hour : hours[0];
    el.scrollTop = Math.max(0, (h - hours[0]) * DENSITY.Default - 24);
    const t = setTimeout(() => setFlash(null), 1400);
    return () => clearTimeout(t);
    // Runs once, for the initial target — later density changes must not re-scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Esc peels one layer at a time: popover, then drawer, then the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (more) setMore(null);
      else if (selected) setSelected(null);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [more, selected, onClose]);

  // The page behind must not scroll while the modal owns the screen.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const items = useMemo(() => buildItems(sessions), [sessions]);

  const columns = useMemo<ColumnSpec[]>(() => {
    if (mode === 'week') {
      return days.map(d => ({ key: String(d.i), day: d, items: items.filter(i => i.kind !== 'more' && i.s.day === d.i) }));
    }
    const dayItems = items.filter(i => i.kind !== 'more' && i.s.day === day);
    const byResource = new Map<string, ResourceSpec & { items: CalItem[] }>();
    for (const i of dayItems) {
      if (i.kind === 'more') continue;
      const key = i.s.res || '—';
      const bucket = byResource.get(key) || { name: key, type: i.type, items: [] };
      bucket.items.push(i);
      byResource.set(key, bucket);
    }
    return [...byResource.values()]
      .sort(
        (a, b) =>
          CAL_TYPE_ORDER.indexOf(a.type) - CAL_TYPE_ORDER.indexOf(b.type) || a.name.localeCompare(b.name)
      )
      .map(r => ({ key: r.name, resource: { name: r.name, type: r.type }, items: r.items }));
  }, [items, mode, day, days]);

  /* Day mode keeps a 150px floor and scrolls sideways — squeezing twelve courts
     into one screen helps nobody. Week mode always fits its seven days. */
  const colMin = mode === 'week' ? 0 : 150;
  const colWidth = Math.max(colMin, (containerWidth - GUTTER) / Math.max(1, columns.length));
  const maxLanes = maxLanesFor(colWidth, mode === 'week' ? 4 : 2);

  const hourList = useMemo(() => {
    const out: number[] = [];
    for (let h = hours[0]; h < hours[1]; h++) out.push(h);
    return out;
  }, [hours]);

  const todayInWeek = days.some(d => d.today);
  const dayIsToday = days.find(d => d.i === day)?.today ?? false;
  const showNow = mode === 'week' ? todayInWeek : dayIsToday;

  const pick = (it: CalItem) => { setSelected(it); setMore(null); };
  const openDay = (i: number) => { setMode('day'); setDay(i); setSelected(null); };

  return (
    <div className="acal acal-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="acal-modal" role="dialog" aria-modal="true" aria-label="Full timeline">
        <header className="a-mh">
          <div className="a-mh-l">
            <WeekNav label={weekLabel} onPrev={onPrevWeek} onNext={onNextWeek} />
            {loading && <BusyPill />}
            <Segmented
              value={mode}
              onChange={m => { setMode(m); setSelected(null); }}
              options={[['week', 'Week'], ['day', 'Day by resource']]}
              ariaLabel="Timeline mode"
            />
            {mode === 'day' && (
              <div className="a-daychips">
                {days.map(d => (
                  <button
                    key={d.i}
                    className={`${d.i === day ? 'on' : ''}${d.today ? ' today' : ''}`}
                    onClick={() => { setDay(d.i); setSelected(null); }}
                  >
                    {d.dow} {d.date}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="a-mh-r">
            {filters}
            <Segmented
              value={density}
              onChange={onDensity}
              options={(Object.keys(DENSITY) as Density[]).map(k => [k, k] as [Density, string])}
              small
              ariaLabel="Row density"
            />
            <button className="a-ic big" onClick={onClose} aria-label="Close timeline"><X className="w-4 h-4" /></button>
          </div>
        </header>

        <div className="a-mb">
          <div className="a-tl-scroll" ref={scrollRef}>
            <div
              className="a-tl-grid"
              style={{ gridTemplateColumns: `${GUTTER}px repeat(${Math.max(1, columns.length)}, minmax(${colMin}px, 1fr))` }}
            >
              <div className="a-tl-corner" />

              {columns.map(c => {
                const st = c.items.reduce(
                  (a, i) => { const x = itemStats(i); a.b += x.booked; a.c += x.cap; return a; },
                  { b: 0, c: 0 }
                );
                const pct = st.c ? Math.round((st.b / st.c) * 100) : 0;
                if (c.day) {
                  const d = c.day;
                  return (
                    <button
                      key={c.key}
                      className={`a-tl-h${d.today ? ' today' : ''}`}
                      onClick={() => openDay(d.i)}
                      title="Open this day by resource"
                    >
                      <span className="dw">{d.dow}</span>
                      <span className="dt">{d.date}</span>
                      <span className="ds">{c.items.length} · {pct}%</span>
                    </button>
                  );
                }
                const r = c.resource!;
                const T = calType(r.type);
                return (
                  <div key={c.key} className="a-tl-h res">
                    <span className="rn"><i style={{ background: T.c }} />{r.name}</span>
                    <span className="ds">{T.label} · {pct}%</span>
                  </div>
                );
              })}

              {!columns.length && <div className="a-tl-h res" />}

              <div className="a-tl-gut" style={{ height: gridHeight }}>
                {hourList.map(h => (
                  <span key={h} style={{ top: (h - hours[0]) * hourPx }}>{String(h).padStart(2, '0')}:00</span>
                ))}
                {showNow && now.min >= hours[0] * 60 && now.min <= hours[1] * 60 && (
                  <span className="nowlbl" style={{ top: topFor(now.min, geo) }}>{minToTime(now.min)}</span>
                )}
              </div>

              {columns.map(c => (
                <TimelineColumn
                  key={c.key}
                  items={c.items}
                  geo={geo}
                  maxLanes={maxLanes}
                  width={colWidth - 4}
                  height={gridHeight}
                  selected={selected}
                  today={mode === 'week' ? !!c.day?.today : dayIsToday}
                  flash={mode === 'week' && flash === c.day?.i}
                  rail={mode === 'week'}
                  now={showNow && now.min >= hours[0] * 60 && now.min <= hours[1] * 60 ? now : null}
                  onPick={pick}
                  onMore={(m, rect) => setMore({ m, rect })}
                />
              ))}

              {!columns.length && (
                <div className="a-tl-empty">Nothing scheduled here with the current filters.</div>
              )}
            </div>
          </div>

          {selected && (
            <SessionDrawer
              it={selected}
              dateLabel={(() => {
                if (selected.kind === 'more') return '';
                const d = days.find(x => x.i === selected.s.day);
                return d ? `${d.dow} ${d.date} ${d.mon}` : selected.s.date;
              })()}
              onClose={() => setSelected(null)}
              onViewBookings={onOpenSession}
              onEdit={onEditSession}
            />
          )}
        </div>

        {more && (
          <MorePopover more={more.m} rect={more.rect} onPick={pick} onClose={() => setMore(null)} />
        )}
      </div>
    </div>
  );
}
